import { Autocomplete, Box, FormControl, Switch, TextField, Tooltip } from '@mui/material'
import React from 'react'
import DecoupledDefaultValueField from '../../components/DecoupledDefaultValueField'
import {
  FIELD_TYPES,
  OUTBOUND_SERVER_ACTION_LIST_DATA_METADATA_KEY,
  OUTBOUND_SERVER_ACTION_LIST_DATA_TYPE,
  OUTBOUND_TRANSLATION_TABLE_OPERATION,
  VALUE_OPERATION,
} from '../../../../utility/constants'
import { textFieldStyle } from '../../components/CreateProcessTemplateDialog'

export const getKey = (txt) => {
  if (!txt) return ''
  return txt
    .toLowerCase() // Convert text to lowercase
    .replace(/[^\w\s_]/g, '') // Remove special characters except underscores
    .replace(/\s+/g, '_')
}

const getKeyOfValue = (curValue, obj) => {
  const keyOfValue = Object.entries(obj).find(([key, value]) => {
    return curValue === value
  })

  return keyOfValue ? keyOfValue[0] : null
}

const AddTranslationUI = ({ translation, setTranslation, fields }) => {
  return (
    <Box
      style={{
        backgroundColor: '#f2f2f2',
        padding: 16,
        borderRadius: 8,
        boxSizing: 'border-box',
        marginTop: 16,
      }}
    >
      <Box
        style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', width: '100%' }}
      >
        <FormControl
          variant="outlined"
          size="small"
          color="primary"
          style={{ minWidth: 120, width: 'calc(50% - 8px)' }}
        >
          <Autocomplete
            id="select-derived-field"
            options={fields}
            size="small"
            sx={textFieldStyle}
            getOptionLabel={(item) => item?.field_template_name}
            value={fields.find((it) => it.id === translation.field_template_id)}
            onChange={(_, v) =>
              setTranslation({
                ...translation,
                field_template_id: v?.id,
                table_operation: null,
                key: getKey(v?.field_template_name),
              })
            }
            renderInput={(params) => (
              <TextField {...params} variant="outlined" label="Select Field" color="primary" />
            )}
          />
        </FormControl>
        <FormControl
          size="small"
          variant="outlined"
          color="primary"
          style={{ minWidth: 120, width: 'calc(50% - 8px)' }}
        >
          <Tooltip
            title={
              translation?.key?.startsWith('x_')
                ? `Keyword starting with x_ is reserved for internal operation`
                : translation?.key
            }
          >
            <TextField
              label="Key"
              sx={{
                ...textFieldStyle,
                '& .MuiInputBase-input': {
                  color: translation?.key?.startsWith('x_') ? 'red' : null,
                },
              }}
              defaultValue={translation.key}
              size="small"
              value={translation.key}
              onChange={(e) => {
                const value = e.target.value
                setTranslation({ ...translation, key: value })
              }}
            />
          </Tooltip>
        </FormControl>
        <FormControl
          size="small"
          variant="outlined"
          color="primary"
          style={{ minWidth: 120, width: 'calc(50% - 8px)' }}
        >
          <DecoupledDefaultValueField
            dataStructure={
              fields.find((it) => it.id === translation.field_template_id)?.data_structure
            }
            listId={fields.find((it) => it.id === translation.field_template_id)?.list_id}
            value={translation.value}
            onValueChange={(v) => setTranslation({ ...translation, value: v })}
            isListDataId={translation.is_list_data_id}
          />
        </FormControl>
        {[FIELD_TYPES.NUMBER, FIELD_TYPES.CURRENCY].includes(
          fields.find((it) => it.id === translation.field_template_id)?.data_structure
        ) && (
          <FormControl
            variant="outlined"
            size="small"
            color="primary"
            style={{ minWidth: 120, width: 'calc(50% - 8px)' }}
          >
            <Autocomplete
              id="select-derived-field"
              options={Object.keys(VALUE_OPERATION)}
              size="small"
              sx={textFieldStyle}
              getOptionLabel={(item) => item}
              value={translation.operation}
              onChange={(_, v) =>
                setTranslation({
                  ...translation,
                  operation: v,
                })
              }
              renderInput={(params) => (
                <TextField {...params} variant="outlined" label="Operation" color="primary" />
              )}
            />
          </FormControl>
        )}
        {FIELD_TYPES.TABLE ===
          fields.find((it) => it.id === translation.field_template_id)?.data_structure && (
          <FormControl
            variant="outlined"
            size="small"
            color="primary"
            style={{ minWidth: 120, width: 'calc(50% - 8px)' }}
          >
            <Autocomplete
              id="select-derived-field"
              options={Object.keys(OUTBOUND_TRANSLATION_TABLE_OPERATION)}
              size="small"
              sx={textFieldStyle}
              getOptionLabel={(item) => item}
              value={getKeyOfValue(
                translation.table_operation,
                OUTBOUND_TRANSLATION_TABLE_OPERATION
              )}
              onChange={(_, v) =>
                setTranslation({
                  ...translation,
                  table_operation: OUTBOUND_TRANSLATION_TABLE_OPERATION[v],
                })
              }
              renderInput={(params) => (
                <TextField {...params} variant="outlined" label="Operation" color="primary" />
              )}
            />
          </FormControl>
        )}
        {FIELD_TYPES.TABLE !==
          fields.find((it) => it.id === translation.field_template_id)?.data_structure && (
          <FormControl
            sx={textFieldStyle}
            size="small"
            variant="outlined"
            color="primary"
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              marginLeft: 16,
            }}
          >
            <Tooltip title="Is Matching Key?">
              <Switch
                color="primary"
                checked={translation.is_matching_key}
                onChange={(_, checked) =>
                  setTranslation({ ...translation, is_matching_key: checked })
                }
              />
            </Tooltip>
          </FormControl>
        )}
        {[FIELD_TYPES.LIST, FIELD_TYPES.LIST_MULTI].includes(
          fields.find((it) => it.id === translation.field_template_id)?.data_structure
        ) && (
          <>
            <FormControl
              variant="outlined"
              size="small"
              color="primary"
              style={{ minWidth: 120, width: 'calc(50% - 8px)' }}
            >
              <Autocomplete
                id="select-derived-field"
                options={Object.values(OUTBOUND_SERVER_ACTION_LIST_DATA_TYPE)}
                size="small"
                sx={textFieldStyle}
                getOptionLabel={(item) => item}
                value={translation.list_data_type}
                defaultValue={OUTBOUND_SERVER_ACTION_LIST_DATA_TYPE.ID}
                onChange={(_, v) =>
                  setTranslation({
                    ...translation,
                    list_data_type: v,
                  })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label="List Data Type"
                    color="primary"
                  />
                )}
              />
            </FormControl>
            {translation.list_data_type === OUTBOUND_SERVER_ACTION_LIST_DATA_TYPE.METADATA ? (
              <FormControl
                variant="outlined"
                size="small"
                color="primary"
                style={{ minWidth: 120, width: 'calc(50% - 8px)' }}
              >
                <Autocomplete
                  id="select-derived-field"
                  options={Object.values(OUTBOUND_SERVER_ACTION_LIST_DATA_METADATA_KEY)}
                  size="small"
                  sx={textFieldStyle}
                  getOptionLabel={(item) => item}
                  value={translation.list_data_metadata_key}
                  onChange={(_, v) =>
                    setTranslation({
                      ...translation,
                      list_data_metadata_key: v,
                    })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="Metadata Key"
                      color="primary"
                    />
                  )}
                />
              </FormControl>
            ) : null}
          </>
        )}
      </Box>
    </Box>
  )
}

export default AddTranslationUI
